import React, { useState } from 'react';
import { memberDisplayName } from 'utils/household';
import AddMemberModal from './AddMemberModal';
import ConfirmModal from './ConfirmModal';

function RemoveConfirmModal({ member, onConfirm, onCancel }) {
  const displayName = memberDisplayName(member);
  return (
    <ConfirmModal
      title={`Remove ${displayName}?`}
      ariaLabel={`Remove ${displayName}`}
      cancelLabel={`Keep ${displayName}`}
      confirmLabel="Yes, remove"
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      This will remove <strong>{displayName}</strong> from your household and clear all
      their items from the cart. This can&apos;t be undone.
    </ConfirmModal>
  );
}

export default function HouseholdMemberTabs({
  members,
  activeMemberId,
  onSelect,
  onAddMember,
  onRemoveMember,
  onUpdateMember,
}) {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [pendingRemove, setPendingRemove] = useState(null);

  const editingIsPrimary = !!editingMember && members[0]?.id === editingMember.id;

  const closeMemberModal = () => {
    setAddModalOpen(false);
    setEditingMember(null);
  };

  const handleRemoveClick = (e, member) => {
    e.stopPropagation();
    setPendingRemove(member);
  };

  const confirmRemove = () => {
    if (pendingRemove) onRemoveMember(pendingRemove.id);
    setPendingRemove(null);
  };

  return (
    <>
      <div className="sf-household-tabs" role="tablist" aria-label="Household members">
        {members.map((member, idx) => {
          const displayName = memberDisplayName(member);
          const isActive = activeMemberId === member.id;
          const canRemove = idx > 0 && members.length > 1;

          return (
            <div
              key={member.id}
              className={`sf-household-tabs__tab-wrap ${isActive ? 'is-active' : ''}`}
            >
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                className="sf-household-tabs__tab"
                onClick={() => onSelect(member.id)}
              >
                {displayName}
                {member.hasFullQuiz && (
                  <span className="sf-household-tabs__quiz-dot" title="Quiz complete">✓</span>
                )}
              </button>

              {/* Edit pencil — opens the same modal used to add a member,
                  pre-filled, so name and allergies can both be edited. */}
              <button
                type="button"
                className="sf-household-tabs__edit"
                aria-label={`Edit ${displayName}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingMember(member);
                }}
                title="Edit name & allergies"
              >
                ✎
              </button>

              {/* Remove × */}
              {canRemove && (
                <button
                  type="button"
                  className="sf-household-tabs__remove"
                  aria-label={`Remove ${displayName}`}
                  onClick={(e) => handleRemoveClick(e, member)}
                  title={`Remove ${displayName}`}
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
        <button
          type="button"
          className="sf-household-tabs__add"
          onClick={() => setAddModalOpen(true)}
        >
          + Add family member
        </button>
      </div>

      <AddMemberModal
        open={addModalOpen || !!editingMember}
        editingMember={editingMember}
        isPrimary={editingIsPrimary}
        onClose={closeMemberModal}
        onSave={(data) => {
          if (editingMember) {
            const updates = {
              name: data.name,
              restrictionTags: data.restrictionTags,
              restrictionLabels: data.restrictionLabels,
            };
            if ('email' in data) updates.email = data.email;
            onUpdateMember(editingMember.id, updates);
          } else {
            onAddMember(data);
          }
          closeMemberModal();
        }}
      />

      {pendingRemove && (
        <RemoveConfirmModal
          member={pendingRemove}
          onConfirm={confirmRemove}
          onCancel={() => setPendingRemove(null)}
        />
      )}
    </>
  );
}
